import React, { lazy } from 'react';
import { Layers, ShieldCheck, Zap, CreditCard } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex flex-col overflow-hidden" aria-label="Presentación de SISTEMA MAESTRO PVC + DVH" data-id="element-124">
      {/* Layered ambient background */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
      background: 'radial-gradient(ellipse 90% 60% at 50% 20%, rgba(22, 41, 74, 0.9) 0%, transparent 70%)'
    }} data-id="element-125" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[120%] h-[600px] pointer-events-none opacity-60" aria-hidden="true" style={{
      background: 'radial-gradient(ellipse 50% 50% at 50% 50%, rgba(212, 168, 67, 0.18) 0%, transparent 60%)',
      filter: 'blur(40px)'
    }} data-id="element-126" />

      <div className="container mx-auto px-4 relative z-10 flex-1 flex flex-col justify-center py-4 md:py-6" data-id="element-127">
        <div className="flex flex-col items-center text-center max-w-5xl mx-auto w-full" data-id="element-128">
          {/* Eyebrow */}
          <div className="section-eyebrow mb-3 md:mb-4" data-id="element-129">
            <Layers className="w-3.5 h-3.5" data-id="element-130" />
            <span data-id="element-131">SISTEMA MAESTRO PVC + DVH</span>
          </div>

          {/* Headline */}
          <h1 className="font-heading font-extrabold heading-tight text-white mb-4 md:mb-5 text-[clamp(1.875rem,5.5vw,4rem)]" data-id="element-132">
            Todo tu taller de PVC + DVH{' '}
            <span className="gold-gradient-text" data-id="element-133">listo. Desde hoy.</span>
          </h1>

          {/* Mockup with luminous platform */}
          <div className="relative mb-4 md:mb-5 w-full" data-id="element-134">
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
            background: 'radial-gradient(ellipse 65% 55% at 50% 50%, rgba(245, 200, 66, 0.32) 0%, rgba(245, 200, 66, 0.12) 30%, transparent 65%)',
            filter: 'blur(20px)'
          }} data-id="element-135" />
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
            background: 'radial-gradient(ellipse 80% 70% at 50% 55%, rgba(255, 248, 230, 0.08) 0%, transparent 55%)'
          }} data-id="element-136" />

            <img src="https://cdn.magicpatterns.com/uploads/uz7iR2XBoKiwU5EPAYF5VA/Texto_del_parrafo_(3).webp" alt="SISTEMA MAESTRO PVC + DVH™ — El sistema definitivo para carpinteros e instaladores" className="mx-auto block h-auto relative z-10 w-[min(46vh,360px)] md:w-[min(48vh,460px)]" style={{
            filter: 'drop-shadow(0 30px 60px rgba(0, 0, 0, 0.7)) drop-shadow(0 0 60px rgba(212, 168, 67, 0.25))'
          }} width={460} height={460} sizes="(min-width: 768px) 460px, 360px"
          // @ts-ignore
          fetchpriority="high" decoding="async" loading="eager" data-id="element-137" />

            <div className="absolute left-1/2 -translate-x-1/2 bottom-0 w-[60%] h-12 pointer-events-none" aria-hidden="true" style={{
            background: 'radial-gradient(ellipse 50% 100% at 50% 0%, rgba(212, 168, 67, 0.18) 0%, transparent 70%)',
            filter: 'blur(10px)'
          }} data-id="element-138" />
          </div>

          {/* Subtitle */}
          <p className="text-base md:text-lg mb-5 md:mb-6 max-w-2xl text-gray-300 leading-relaxed" data-id="element-139">
            El sistema definitivo para fabricar PVC + DVH sin errores, sin
            desperdicio de material y sin improvisación.{' '}
            <span className="text-white font-semibold" data-id="element-140">Todo resuelto.</span>
          </p>

          {/* Primary CTA */}
          <button onClick={() => {
          // Kick off the lazy Pricing chunk immediately
          import('./Pricing').catch(() => {});
          // Gentle ease-out cubic — fast start, soft landing.
          // Avoids the "slow at the beginning, jerky in the middle"
          // feel that ease-in-out creates over long distances.
          const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
          const getTargetEl = () => document.getElementById('cta-comprar') || document.getElementById('pack-pro') || document.getElementById('elegir-plan') || document.getElementById('pricing');
          const computeTargetY = (el: HTMLElement) => {
            const rect = el.getBoundingClientRect();
            // Bottom of #cta-comprar pinned ~24px above viewport bottom
            // so both packs and the gold button remain visible together.
            return Math.max(0, window.scrollY + rect.bottom - window.innerHeight + 24);
          };
          const startY = window.scrollY;
          const duration = 900; // ms — smooth but not slow
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          // Wait briefly for the lazy Pricing section to mount so
          // its layout is settled before we animate.
          const waitDeadline = performance.now() + 1500;
          const startAnim = () => {
            const el = getTargetEl();
            if (!el) {
              if (performance.now() < waitDeadline) {
                requestAnimationFrame(startAnim);
              }
              return;
            }
            if (reduceMotion) {
              window.scrollTo(0, computeTargetY(el));
              return;
            }
            const startTime = performance.now();
            let animDone = false;
            const step = (now: number) => {
              if (animDone) return;
              const elapsed = now - startTime;
              const t = Math.min(elapsed / duration, 1);
              // Recompute target each frame — the page may still be
              // growing as below-the-fold sections finish rendering.
              // This keeps the landing position accurate even with
              // layout shifts.
              const liveEl = getTargetEl();
              const targetY = liveEl ? computeTargetY(liveEl) : startY;
              const distance = targetY - startY;
              window.scrollTo(0, startY + distance * easeOutCubic(t));
              if (t < 1) requestAnimationFrame(step);else animDone = true;
            };
            // Cancel our animation if the user starts scrolling manually
            const cancel = () => {
              animDone = true;
            };
            window.addEventListener('wheel', cancel, {
              once: true,
              passive: true
            });
            window.addEventListener('touchstart', cancel, {
              once: true,
              passive: true
            });
            requestAnimationFrame(step);
          };
          startAnim();
        }} className="group relative inline-flex items-center justify-center w-full sm:w-auto px-8 md:px-10 py-4 md:py-5 text-base md:text-lg font-bold text-bg bg-gradient-to-b from-gold-light to-gold rounded-xl hover:from-gold hover:to-gold-dark transition-all uppercase tracking-wider cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold gold-box-glow" data-id="element-141">
            <span className="relative z-10 flex items-center gap-2" data-id="element-142">
              🏗️ Quiero mi Sistema Maestro ahora
              <span className="transition-transform group-hover:translate-x-1" data-id="element-143">
                →
              </span>
            </span>
          </button>

          <p className="text-xs text-gray-400 mt-3 uppercase tracking-widest" data-id="element-144">
            Oferta de lanzamiento — Acceso inmediato
          </p>
        </div>
      </div>

      {/* Trust strip + signals — pushed below the fold, still part of hero section */}
      <div className="relative z-10 container mx-auto px-4 pb-12 md:pb-16" data-id="element-145">
        <div className="flex flex-col items-center text-center max-w-5xl mx-auto" data-id="element-146">
          <div className="flex flex-wrap justify-center gap-3 mt-8 mb-6 max-w-3xl" role="list" aria-label="Características principales" data-id="element-147">
            {[{
            icon: '📄',
            label: '100% PDF'
          }, {
            icon: '⚡',
            label: 'Acceso en 5 min'
          }, {
            icon: '🇦🇷',
            label: 'Corredizo → Batiente → Oscilobatiente'
          }, {
            icon: '🔧',
            label: 'Todas las tipologías'
          }].map((item, i) => <div key={i} className="flex items-center gap-2 bg-white/[0.03] backdrop-blur-sm px-4 py-2.5 rounded-full border border-white/10 hover:border-gold/30 transition-colors" role="listitem" data-id="element-148">
                <span className="text-base" aria-hidden="true" data-id="element-149">
                  {item.icon}
                </span>
                <span className="font-medium text-xs md:text-sm text-gray-200 whitespace-nowrap" data-id="element-150">
                  {item.label}
                </span>
              </div>)}
          </div>

          <div className="flex flex-wrap justify-center items-center gap-x-8 gap-y-3 text-sm text-gray-400" data-id="element-151">
            <div className="flex items-center gap-2" data-id="element-152">
              <ShieldCheck className="w-4 h-4 text-gold" data-id="element-153" />
              <span data-id="element-154">Garantía 7 días</span>
            </div>
            <span className="text-white/10" aria-hidden="true" data-id="element-155">
              ·
            </span>
            <div className="flex items-center gap-2" data-id="element-156">
              <Zap className="w-4 h-4 text-gold" data-id="element-157" />
              <span data-id="element-158">Pago seguro</span>
            </div>
            <span className="text-white/10" aria-hidden="true" data-id="element-159">
              ·
            </span>
            <div className="flex items-center gap-2" data-id="element-160">
              <CreditCard className="w-4 h-4 text-gold" data-id="element-161" />
              <span data-id="element-162">Mercado Pago</span>
            </div>
          </div>

          <div className="w-full max-w-5xl mt-10" data-id="element-163">
            <div className="gold-hairline" data-id="element-164" />
          </div>
        </div>
      </div>
    </section>;
}